import { PostNow } from 'blaze-ui/icons/32/PostNow'
import { MarkPosted } from 'blaze-ui/icons/32/MarkPosted'
import { Calendar, Reminder } from 'blaze-ui/icons/32'
import { DesignChannel } from 'components/NewFileModal/Types'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ProviderIcon } from 'components/PostPreviewModal/ProviderIcon'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { AssigneeType, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import dayjs from 'dayjs'
import { useCompleteDesignAction } from '../utils/useCompleteDesignAction'
import { DesignActionType } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import {
  PostOptionsPage,
  providerFromDesignChannelType,
  providerNameFromChannelType,
} from 'components/PostPreviewModal/Types'
import { useRootStore } from 'stores/RootStore'
import { useModals } from 'almanac-ui/Modal'
import { PostPreviewDrawer } from 'components/PostPreviewModal/PostPreviewDrawer'
import { PostPreviewModal } from 'components/PostPreviewModal/PostPreviewModal'
import { Key } from '@react-types/shared'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Icon } from 'almanac-ui/icons/Types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

export enum ActionKeys {
  PostNow = 'PostNow',
  SchedulePost = 'SchedulePost',
  Connect = 'Connect',
  SetReminder = 'SetReminder',
  MarkPosted = 'MarkPosted',
  ChooseHowToPublish = 'ChooseHowToPublish',
}

type SchedulingAction = {
  key: ActionKeys
  label: string
  icon: Icon
}

interface Props {
  fileId: string
  channel: DesignChannel
}

export const useSchedulingOptions = ({ fileId, channel }: Props) => {
  const integrationsQuery = useExternalIntegrations()
  const completeActionMutation = useCompleteDesignAction()
  const provider = providerFromDesignChannelType[channel as DesignChannel] || ExternalIntegrationEnum.Instagram
  const isPostPreviewV2 = useFlipperFlag('post_preview_v2')
  const { isMobile } = useResponsiveDesign()
  const hasIntegration = !!(
    channel && integrationsQuery.data?.external_integrations.find((i) => i.provider === provider)
  )
  const { status, updateStatus, updatePostAt } = usePostProperties({
    id: fileId,
    type: AssigneeType.Design,
  })
  const isScheduledOrPosted = [
    PostStatusPropertyValue.AutoPostScheduled,
    PostStatusPropertyValue.ReminderScheduled,
    PostStatusPropertyValue.Posted,
  ].includes(status.value)
  const providerName = providerNameFromChannelType[channel] || 'Instagram'

  const handleMarkPosted = () => {
    updatePostAt(dayjs().toISOString())
    updateStatus(PostStatusPropertyValue.Posted)
    completeActionMutation.mutate({ action: DesignActionType.SchedulePost })
  }

  const actions = {
    [ActionKeys.PostNow]: {
      key: ActionKeys.PostNow,
      label: 'Post now',
      icon: PostNow,
    },
    [ActionKeys.SchedulePost]: {
      key: ActionKeys.SchedulePost,
      label: 'Schedule post for later',
      icon: Calendar,
    },
    [ActionKeys.Connect]: {
      key: ActionKeys.Connect,
      label: `Connect to ${providerName}`,
      icon: () => <ProviderIcon provider={provider} size={32} />,
    },
    [ActionKeys.SetReminder]: {
      key: ActionKeys.SetReminder,
      label: 'Set a reminder to post',
      icon: Reminder,
    },
    [ActionKeys.MarkPosted]: {
      key: ActionKeys.MarkPosted,
      label: 'Mark as posted',
      icon: MarkPosted,
    },
    [ActionKeys.ChooseHowToPublish]: {
      key: ActionKeys.ChooseHowToPublish,
      label: 'Choose how to publish',
      icon: PostNow,
    },
  }

  const enabledActions: SchedulingAction[] = []
  if (isPostPreviewV2 && !isMobile) {
    if (
      status.value !== PostStatusPropertyValue.Posted &&
      status.value !== PostStatusPropertyValue.AutoPostScheduled &&
      status.value !== PostStatusPropertyValue.ReminderScheduled
    ) {
      enabledActions.push(actions[ActionKeys.ChooseHowToPublish])
    }
  } else if (!hasIntegration) {
    enabledActions.push(actions[ActionKeys.Connect])
  } else {
    if (status.value !== PostStatusPropertyValue.Posted) {
      enabledActions.push(actions[ActionKeys.PostNow])
      enabledActions.push(actions[ActionKeys.MarkPosted])

      if (status.value !== PostStatusPropertyValue.AutoPostScheduled) {
        enabledActions.push(actions[ActionKeys.SchedulePost])
      }

      if (status.value !== PostStatusPropertyValue.ReminderScheduled) {
        enabledActions.push(actions[ActionKeys.SetReminder])
      }
    }
  }

  return { enabledActions, isScheduledOrPosted, handleMarkPosted }
}

interface OpenProps {
  fileId: string
  scheduledPostId: number
  channel: DesignChannel
}

export const useHandleAction = ({ fileId, scheduledPostId, channel }: OpenProps) => {
  const { drawersStore } = useRootStore()
  const { openModal } = useModals()
  const { handleMarkPosted } = useSchedulingOptions({ fileId, channel })
  const { isMobile } = useResponsiveDesign()

  const openPostPreview = (initialPage: PostOptionsPage) => {
    if (isMobile) {
      drawersStore.openDrawer(PostPreviewDrawer, { initialPage, fileId, scheduledPostId, channel, target: 'design' })
    } else {
      openModal(PostPreviewModal, { initialPage, fileId, scheduledPostId, channel, target: 'design' })
    }
  }

  const handleAction = (key: Key) => {
    switch (key) {
      case ActionKeys.PostNow:
      case ActionKeys.ChooseHowToPublish:
        openPostPreview(PostOptionsPage.PostDetails)
        break
      case ActionKeys.SchedulePost:
        openPostPreview(PostOptionsPage.Schedule)
        break
      case ActionKeys.Connect:
        openPostPreview(PostOptionsPage.PostDetails)
        break
      case ActionKeys.SetReminder:
        openPostPreview(PostOptionsPage.Reminder)
        break
      case ActionKeys.MarkPosted:
        handleMarkPosted()
        break
    }
  }

  return handleAction
}
